// Phase 1A Phase 9C.1 — Motion / transition style helpers.
//
// Reusable CSSProperties exports that wrap the existing motion tokens
// (--sz-duration-* + --sz-ease-*) into ready-to-spread style objects.
// Consumers should spread these into a component's style object rather
// than rewrite the transition string.
//
// Trust contract:
//   - Token-only. No raw durations or easing curves.
//   - No keyframes. No globals.css edits.
//   - No motion implying live data (i.e. no animation tied to values
//     that aren't actually changing).
//   - All motion respects prefers-reduced-motion via the existing
//     globals.css discipline (consumers do not need to opt in).
//   - Adoption begins in 9C.3+. No UI surface consumes these in 9C.1.

import type { CSSProperties } from "react";

// ─── Pure transition helpers ────────────────────────────────────
//
// Each "transition*" export sets a transition shorthand spanning the
// most common animatable properties. Components can spread the helper
// then set state-driven values (background, transform, box-shadow)
// and the transition is wired automatically.

const TRANSITION_PROPS = "background, color, border-color, box-shadow, transform, opacity";

export const transitionFast: CSSProperties = {
  transition: `${TRANSITION_PROPS} var(--sz-duration-fast) var(--sz-ease-standard)`,
};

export const transitionStandard: CSSProperties = {
  transition: `${TRANSITION_PROPS} var(--sz-duration-standard) var(--sz-ease-standard)`,
};

export const transitionSlow: CSSProperties = {
  transition: `${TRANSITION_PROPS} var(--sz-duration-slow) var(--sz-ease-standard)`,
};

// ─── Hover affordances (spread together with transition*) ──────

export const hoverLift: CSSProperties = {
  transform: "translateY(-1px)",
};

export const hoverLiftMedium: CSSProperties = {
  transform: "translateY(-2px)",
};

export const pressShrink: CSSProperties = {
  transform: "scale(0.98)",
};

export const pressShrinkSubtle: CSSProperties = {
  transform: "scale(0.99)",
};

// ─── Focus affordance (composes the new focus-ring tokens) ─────

export const focusMotion: CSSProperties = {
  outline: "none",
  boxShadow: "var(--sz-focus-ring)",
};

export const focusMotionDanger: CSSProperties = {
  outline: "none",
  boxShadow: "var(--sz-focus-ring-danger)",
};

// ─── Enter / appear helpers (one-shot, never imply realtime) ────
//
// Used for first-mount card appearance only. Consumers must not
// re-trigger these on data updates — that would imply live data.

export const enterFade: CSSProperties = {
  opacity: 1,
  transition: `opacity var(--sz-duration-standard) var(--sz-ease-enter)`,
};

export const enterRiseFade: CSSProperties = {
  opacity: 1,
  transform: "translateY(0)",
  transition: `opacity var(--sz-duration-standard) var(--sz-ease-enter), transform var(--sz-duration-standard) var(--sz-ease-enter)`,
};
